/**
 * Recently inserted mentions, per surface.
 *
 * The picker has no recency signal from the server: items arrive in a static
 * category order, so an empty query dumps every agent alphabetically and the
 * one you pick daily sits wherever the alphabet put it. Remembering the last
 * few selections locally is what makes a mention picker feel immediate, and it
 * needs no API change.
 */

const LIMIT = 8;

const storageKey = (scope: string): string => `cotera.mention-recents.${scope}`;

/**
 * Storage throws outright in some contexts (Safari private mode, embedded
 * webviews, site data blocked), so every access is guarded. Recency is a
 * convenience: losing it must never take the picker down with it.
 */
const safeRead = (key: string): string[] => {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((v): v is string => typeof v === 'string')
      : [];
  } catch {
    return [];
  }
};

const safeWrite = (key: string, ids: string[]): void => {
  try {
    window.localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    // Nothing to do: the picker works fine without recency.
  }
};

export const readRecentIds = (scope: string): string[] =>
  safeRead(storageKey(scope)).slice(0, LIMIT);

/** Moves `id` to the front, de-duplicating and capping the list. */
export const recordRecentId = (scope: string, id: string): string[] => {
  if (id.length === 0) {
    return readRecentIds(scope);
  }
  const next = [
    id,
    ...safeRead(storageKey(scope)).filter((x) => x !== id),
  ].slice(0, LIMIT);
  safeWrite(storageKey(scope), next);
  return next;
};

/**
 * Orders `ids` by how recently each was used, dropping ids that are no longer
 * in the catalog. Returns at most `LIMIT` entries.
 */
export const pickRecent = <T extends { id: string }>(
  items: T[],
  recentIds: string[]
): T[] => {
  const byId = new Map(items.map((item) => [item.id, item]));
  return recentIds
    .map((id) => byId.get(id))
    .filter((item): item is T => item !== undefined);
};

export const RECENTS_LIMIT = LIMIT;
